import * as React from 'react';

import { enumLotStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  Col,
  DataGrid,
  FloatingToolbar,
  ListStatsCard,
  type ListStatsCardProps,
  Pagination,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCirclePile, LucideTrash2, LucideX } from 'lucide-react';

import { deallocateFromLotAtom } from '../../_graphql/mutationInventoryLot';
import { type LotAllocationRecord } from '../../_graphql/queryInventoryLot';
import * as state from '../../_state/lotDetail.state';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';

import { LotDeallocateConfirmDialog } from './LotDeallocateConfirmDialog';
import { useLotAllocationColumns } from './useLotAllocationColumns';

export const LotDetailOverviewContent = () => {
  const t = useTranslations();
  const { formatQuantityValue } = useInventoryUnit();
  const columns = useLotAllocationColumns();
  const toast = useToast();

  const lotId = useAtomValue(state.$lotId);
  const { data, isFetching, loadingState, refetch } = useAtomValue(
    state.$remoteLotDetail,
  );
  const [allocationsLimit, setAllocationsLimit] = useAtom(
    state.$allocationsLimit,
  );
  const [allocationsOffset, setAllocationsOffset] = useAtom(
    state.$allocationsOffset,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedAllocationIds);
  const deallocate = useSetAtom(deallocateFromLotAtom({}));

  const { lot, quantitySummary, allocations, allocationsTotalCount } =
    React.useMemo(
      () => ({
        lot: data,
        quantitySummary: data?.quantitySummary ?? [],
        allocations: data?.allocations ?? [],
        allocationsTotalCount: data?.allocationsTotalCount ?? 0,
      }),
      [data],
    );

  const isActive = lot?.status === enumLotStatus.Active;

  const stats: ListStatsCardProps['stats'] = React.useMemo(() => {
    if (!lot || quantitySummary.length === 0) return [];
    const base: ListStatsCardProps['stats'] = [
      {
        key: 'total-items',
        label: t.lotsListView.detail.summary.totalItems,
        value: lot.quantitySummary.length,
        icon: <LucideCirclePile />,
      },
    ];
    quantitySummary.forEach((summary) => {
      base.push({
        key: summary.itemId,
        label: summary.itemName,
        value: formatQuantityValue(summary.quantity, summary.quantityUnit),
      });
    });
    return base;
  }, [lot, quantitySummary, t, formatQuantityValue]);

  const emptyPanelProps = {
    description: t.lotsListView.detail.noAllocations,
    centered: true,
  };

  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setAllocationsLimit(newLimit);
      setAllocationsOffset(newOffset);
    },
    [setAllocationsLimit, setAllocationsOffset],
  );

  const [isConfirmingDeallocate, setIsConfirmingDeallocate] =
    React.useState(false);

  const selectedCount =
    selectedKeys === 'all' ? allocations.length : selectedKeys.size;

  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);

  const handleConfirmDeallocate = React.useCallback(async () => {
    const allocationIds =
      selectedKeys === 'all'
        ? allocations.map((a) => a.id)
        : Array.from(selectedKeys).map(String);
    if (allocationIds.length === 0) return;

    try {
      await deallocate({ allocationIds });
      setSelectedKeys(new Set());
      setIsConfirmingDeallocate(false);
      refetch?.();
      toast.success(t.lotsListView.detail.deallocateSuccess);
    } catch (error) {
      console.error(error);
      toast.error(t.lotsListView.detail.deallocateError);
    }
  }, [
    selectedKeys,
    allocations,
    deallocate,
    setSelectedKeys,
    refetch,
    toast,
    t,
  ]);

  React.useEffect(() => {
    setSelectedKeys(new Set());
  }, [lotId, setSelectedKeys]);

  return (
    <>
      <Col className="@LotDetailOverviewContent p-4" gap5>
        {lot && stats.length > 0 && <ListStatsCard stats={stats} />}

        <div className="flex-1">
          <DataGrid<LotAllocationRecord>
            isContained
            isLoading={loadingState.isInitialLoading || isFetching}
            columns={columns}
            dataSource={allocations}
            rowKey="id"
            aria-label={t.lotsListView.detail.table.aria}
            selectionMode={isActive ? 'multiple' : 'none'}
            selectedKeys={isActive ? selectedKeys : undefined}
            onSelectionChange={isActive ? setSelectedKeys : undefined}
            className="w-full text-sm"
            fullWidth
            emptyPanelProps={emptyPanelProps}
          />
        </div>

        {allocationsTotalCount > allocationsLimit && (
          <div className="mt-4">
            <Pagination
              limit={allocationsLimit}
              offset={allocationsOffset}
              total={allocationsTotalCount}
              onChange={handlePaginationChange}
            />
          </div>
        )}

        <FloatingToolbar visible={isActive && selectedCount > 0}>
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="flex flex-row items-center gap-2 text-sm">
              <Badge color="primary" size="small">
                {selectedCount}
              </Badge>
              {t.lotsListView.detail.toolbar.selected}
            </div>
            <div className="flex flex-row items-center gap-2">
              <Button
                variant="outline"
                color="failure"
                icon={<LucideTrash2 className="size-4" />}
                onPress={() => setIsConfirmingDeallocate(true)}
              >
                {t.lotsListView.detail.toolbar.deallocate}
              </Button>
              <LucideX
                className="size-4 cursor-pointer text-neutral/80"
                onClick={handleClearSelection}
              />
            </div>
          </div>
        </FloatingToolbar>
      </Col>

      <LotDeallocateConfirmDialog
        isOpen={isConfirmingDeallocate}
        count={selectedCount}
        onConfirm={handleConfirmDeallocate}
        onCancel={() => setIsConfirmingDeallocate(false)}
      />
    </>
  );
};
